import type { ReactNode } from 'react';
import type { StockSurplusLocation } from '@erp/rpc';
import type { SurplusComponentTrace } from '@erp/shared-types';
import { T, useGT } from 'gt-react';
import { LedgerGroupRow, LedgerRow } from './CompositionLedger';
import { compositionWarning, traceReason } from './compositionText';
import {
  minimumTraceComponents,
  retentionExpression,
  selectedTraceComponents,
  surplusQuantity,
  visibleCompositionWarnings,
} from './surplusTracePresentation';
import { SurplusTraceRow } from './SurplusTraceRow';

type Explanation = NonNullable<StockSurplusLocation['explanation']>;

/** The whole location in one line, before the ledger that justifies it. */
function LocationArithmetic({
  row,
  explanation,
  selected,
}: {
  row: StockSurplusLocation;
  explanation: Explanation;
  selected: SurplusComponentTrace[];
}) {
  const gt = useGT();
  if (row.quantity === null) return null;
  return (
    <p className="text-muted-foreground tabular-nums">
      {gt('max(0, {stock} − {deductions} − {retained}) = {surplus}', {
        context:
          'surplus arithmetic with server evaluated quantities and retention maximum',
        stock: surplusQuantity(row.onHand),
        deductions: surplusQuantity(explanation.deductions),
        retained: retentionExpression(selected, explanation.retained),
        surplus: surplusQuantity(row.quantity),
      })}
    </p>
  );
}

function QuantityBasisNote({ basis }: { basis: Explanation['quantityBasis'] }) {
  if (basis === 'native_stock_unit_unspecified')
    return (
      <p>
        <T context="surplus native stock units explanation">
          Source stock quantity (unit unspecified). No conversion or
          cross-source comparison.
        </T>
      </p>
    );
  if (basis === 'unverified_comparison_unit')
    return (
      <p>
        <T context="surplus unavailable comparison unit">
          Comparison units are not verified.
        </T>
      </p>
    );
  return null;
}

function LocationNotes({
  row,
  explanation,
  minimumUsed,
}: {
  row: StockSurplusLocation;
  explanation: Explanation;
  minimumUsed: boolean;
}) {
  const gt = useGT();
  return (
    <div className="space-y-0.5 pl-6 text-muted-foreground">
      {row.status === 'unavailable' && (
        <p className="font-medium text-foreground">
          <T context="surplus unavailable result explanation">
            Unavailable. Missing selected data is not zero.
          </T>
        </p>
      )}
      {row.reason && <p>{traceReason(row.reason, gt)}</p>}
      <QuantityBasisNote basis={explanation.quantityBasis} />
      {visibleCompositionWarnings(explanation.warnings, minimumUsed).map(
        (warning) => (
          <p key={warning}>{compositionWarning(warning, gt)}</p>
        ),
      )}
    </div>
  );
}

function TraceGroup({
  title,
  operatorPhrase,
  value,
  components,
  maximumKnown = false,
}: {
  title: ReactNode;
  operatorPhrase: ReactNode;
  value: number | null;
  components: SurplusComponentTrace[];
  maximumKnown?: boolean;
}) {
  return (
    <div>
      <LedgerGroupRow
        operator="−"
        title={title}
        operatorPhrase={operatorPhrase}
        value={surplusQuantity(value)}
      />
      {components.map((component) => (
        <SurplusTraceRow
          key={component.id}
          row={component}
          maximumKnown={maximumKnown}
        />
      ))}
    </div>
  );
}

function LocationLedger({
  row,
  explanation,
  selected,
}: {
  row: StockSurplusLocation;
  explanation: Explanation;
  selected: SurplusComponentTrace[];
}) {
  const gt = useGT();
  return (
    <div>
      <LedgerRow
        label={
          <T context="surplus physical stock input">Physical stock on hand</T>
        }
        detail={
          row.snapshotDate
            ? gt('Inventory snapshot: {date}', {
                context: 'surplus physical stock source timestamp',
                date: row.snapshotDate,
              })
            : null
        }
        value={surplusQuantity(row.onHand)}
      />
      <TraceGroup
        title={<T context="surplus deductions subtotal">Deductions</T>}
        operatorPhrase={
          <T context="surplus deduction operator">sum of selected</T>
        }
        value={explanation.deductions}
        components={selected.filter((item) => item.group === 'deduction')}
      />
      <LedgerRow
        operator="="
        rule
        emphasis
        label={
          <T context="surplus policy dependent stock subtotal">
            Stock after selected deductions
          </T>
        }
        value={surplusQuantity(row.available)}
      />
      <TraceGroup
        title={<T context="surplus retention subtotal">Retained protection</T>}
        operatorPhrase={
          <T context="surplus protection operator">largest of selected</T>
        }
        value={explanation.retained}
        components={selected.filter((item) => item.group === 'retention')}
        maximumKnown={explanation.retained !== null}
      />
      <LedgerRow
        operator="="
        rule
        emphasis
        label={
          <T context="surplus clamped result">Surplus · never below zero</T>
        }
        value={surplusQuantity(row.quantity)}
      />
    </div>
  );
}

function SalesHistory({
  row,
  selected,
}: {
  row: StockSurplusLocation;
  selected: SurplusComponentTrace[];
}) {
  const gt = useGT();
  const sales = selected.find(
    (component) => component.kind === 'historical_customer_sales',
  );
  if (!sales) return null;
  return (
    <div className="space-y-0.5 pl-6 text-muted-foreground">
      <p>
        {gt('Covered months: {months}', {
          context: 'surplus sales source covered observation months',
          months:
            row.coveredMonths.length > 0
              ? row.coveredMonths.join(', ')
              : gt('Unavailable', {
                  context: 'surplus covered periods unavailable',
                }),
        })}
      </p>
      {sales.state === 'estimated' && (
        <p>
          <T context="surplus partial history constant rate method">
            Estimate at a constant monthly rate: average sales per covered month
            multiplied by the selected protection target. Actual surplus may
            differ for new or seasonal items.
          </T>
        </p>
      )}
    </div>
  );
}

const DISCLOSURE_SUMMARY =
  'w-fit cursor-pointer rounded-sm py-1 font-medium focus-visible:outline focus-visible:outline-2 focus-visible:outline-ring';

function MinimumDisclosure({
  explanation,
  selected,
  minimumUsed,
}: {
  explanation: Explanation;
  selected: SurplusComponentTrace[];
  minimumUsed: boolean;
}) {
  return (
    <details className="pl-6">
      <summary className={DISCLOSURE_SUMMARY}>
        {minimumUsed ? (
          <T context="surplus composed minimum inputs disclosure">
            Composed minimum · largest of inputs
          </T>
        ) : (
          <T context="minimum not consumed by surplus">
            Minimum · not used in surplus
          </T>
        )}
      </summary>
      {minimumTraceComponents(selected).map((component) => (
        <SurplusTraceRow
          key={component.id}
          row={component}
          maximumKnown={explanation.minimum !== null}
        />
      ))}
      <LedgerRow
        operator="="
        rule
        emphasis
        label={
          <T context="surplus composed minimum subtotal">Composed minimum</T>
        }
        value={surplusQuantity(explanation.minimum)}
      />
    </details>
  );
}

function OmittedComponents({
  components,
}: {
  components: SurplusComponentTrace[];
}) {
  const omitted = components.filter(
    (component) => component.state === 'not_selected',
  );
  if (omitted.length === 0) return null;
  return (
    <details className="pl-6">
      <summary className={DISCLOSURE_SUMMARY}>
        <T context="omitted surplus components disclosure">
          Components not selected
        </T>
      </summary>
      {omitted.map((component) => (
        <SurplusTraceRow key={component.id} row={component} />
      ))}
    </details>
  );
}

export function SurplusLocationExplanation({
  row,
  name,
}: {
  row: StockSurplusLocation;
  name: string;
}) {
  const explanation = row.explanation;
  if (!explanation) return null;
  const selected = selectedTraceComponents(explanation.components);
  const minimumUsed = selected.some(
    (component) => component.kind === 'composed_minimum',
  );
  return (
    <section className="space-y-1.5 border-t pt-2">
      <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-0.5">
        <h4 className="font-medium">{name}</h4>
        <LocationArithmetic
          row={row}
          explanation={explanation}
          selected={selected}
        />
      </div>
      <LocationLedger row={row} explanation={explanation} selected={selected} />
      <LocationNotes
        row={row}
        explanation={explanation}
        minimumUsed={minimumUsed}
      />
      <SalesHistory row={row} selected={selected} />
      <MinimumDisclosure
        explanation={explanation}
        selected={selected}
        minimumUsed={minimumUsed}
      />
      <OmittedComponents components={explanation.components} />
    </section>
  );
}
